// src/pages/gene.codes/bloods/BloodAnalysiscomponents/BloodTestResultsTable.tsx
import React, { useState, useEffect, useMemo } from "react";
import { Tag, Input, Select, Button, Tooltip, Typography } from "antd";
import { SearchOutlined, ClearOutlined, InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";

import { DataTable, SectionCard } from "../../../../components/shared";



import ResultRangeBar from "./ResultRangeBar";
import HistoricalTrendChart from "./HistoricalTrendChart";

import { BloodTestResult } from "../../../../types/lab/BloodTestTypes";
import { Test } from "../../../../types/healthcare/TestTypes";
import { calculateResultSeverity, resolveResultRange, ResultSeverityData } from "../../../../utils/geneCodes/bloodResultUtils";
import { RangeContext } from "../../../../utils/geneCodes/referenceRange";
import RangeSourceTag from "../../../../components/blood/RangeSourceTag";
const { Text } = Typography;

const severityDot = (severity: ResultSeverityData) => (
	<span title={severity.label} style={{ width: 10, height: 10, borderRadius: "50%", background: severity.color, display: "inline-block", flexShrink: 0 }} />
);

const resultValueStyle = (severity: ResultSeverityData): React.CSSProperties =>
	severity.isAbnormal ? { fontWeight: 700, fontSize: "12px", color: severity.color } : { fontWeight: 600, fontSize: "12px" };

interface BloodTestResultsTableProps {
	results: BloodTestResult[];
	category: string | null;
	rangeContext: RangeContext;
	onResultClick: (result: BloodTestResult) => void;
	selectedResult: BloodTestResult | null;
	allTests?: Test[];
}

const BloodTestResultsTable: React.FC<BloodTestResultsTableProps> = ({ results, category, rangeContext, onResultClick, selectedResult, allTests = [] }) => {
	const [globalFilter, setGlobalFilter] = useState<string>("");
	const [flagFilter, setFlagFilter] = useState<string | null>(null);
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	// Resolved once per analyte — the filter, both column sets and the summary counts all
	// read it, and Ant Design re-runs every cell renderer on each scroll.
	const severityByResult = useMemo(() => {
		const map = new Map<number, ResultSeverityData>();
		results.forEach((result) => map.set(result.id, calculateResultSeverity(result, rangeContext)));
		return map;
	}, [results, rangeContext]);

	const severityOf = (result: BloodTestResult): ResultSeverityData =>
		severityByResult.get(result.id) ?? calculateResultSeverity(result, rangeContext);

	const rangeLabel = (result: BloodTestResult) => {
		const { display, source } = resolveResultRange(result, rangeContext);
		return (
			<div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
				<Text type="secondary" style={{ fontSize: 10 }}>{display || "—"}</Text>
				<RangeSourceTag source={source} labFlag={result.lab_flag} />
			</div>
		);
	};

	const flagOptions = [
		{ label: "All Results", value: null },
		{ label: "Normal", value: "normal" },
		{ label: "Slightly Abnormal", value: "mild" },
		{ label: "Abnormal", value: "moderate" },
		{ label: "Very Abnormal", value: "severe" },
		{ label: "Critical", value: "critical" },
	];

	const filteredResults = results.filter((result) => {
		if (flagFilter) {
			const severityData = severityOf(result);
			if (flagFilter === "normal" && severityData.severity !== "NORMAL") return false;
			if (flagFilter === "mild" && severityData.severity !== "MILD") return false;
			if (flagFilter === "moderate" && severityData.severity !== "MODERATE") return false;
			if (flagFilter === "severe" && severityData.severity !== "SEVERE") return false;
			if (flagFilter === "critical" && severityData.severity !== "CRITICAL") return false;
		}

		if (globalFilter) {
			const searchTerm = globalFilter.toLowerCase();
			return result.test_name?.toLowerCase().includes(searchTerm) || result.test_code?.toLowerCase().includes(searchTerm) || result.result_value?.toLowerCase().includes(searchTerm);
		}

		return true;
	});

	const mobileColumns: ColumnsType<BloodTestResult> = [
		{
			title: "Test",
			dataIndex: "test_name",
			key: "test_name",
			render: (text, record) => {
				const severityData = severityOf(record);
				return (
					<div style={{ width: "100%" }}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
							<div style={{ flex: 1 }}>
								{severityData.isAbnormal ? (
									<div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "2px" }}>
										{severityDot(severityData)}
										<div style={{ fontWeight: 600, fontSize: "12px" }}>{text}</div>
									</div>
								) : (
									<div style={{ fontWeight: 600, fontSize: "12px", marginBottom: "2px" }}>{text}</div>
								)}
								{record.test_code && <div style={{ fontSize: "10px", color: "var(--mg-text-secondary)" }}>{record.test_code}</div>}
							</div>
							<div style={{ marginLeft: "8px", textAlign: "right" }}>
								<div style={resultValueStyle(severityData)}>{record.result_value}</div>
								{record.units && <div style={{ fontSize: "10px", color: "var(--mg-text-secondary)" }}>{record.units}</div>}
							</div>
						</div>
						<div style={{ marginBottom: "8px" }}>
							<ResultRangeBar result={record} rangeContext={rangeContext} compact={true} showLabels={false} height={8} />
						</div>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
								<Tag color={severityData.tagColor} style={{ fontSize: "10px", margin: 0 }}>
									{severityData.label}
								</Tag>
								{rangeLabel(record)}
							</div>
							{record.comments && (
								<Tooltip title={record.comments} placement="left">
									<InfoCircleOutlined style={{ fontSize: "12px" }} />
								</Tooltip>
							)}
						</div>
					</div>
				);
			},
		},
	];

	const desktopColumns: ColumnsType<BloodTestResult> = [
		{
			title: "Test Name",
			dataIndex: "test_name",
			key: "test_name",
			width: 100,
			sorter: (a, b) => a.test_name.localeCompare(b.test_name),
			render: (text, record) => {
				const severityData = severityOf(record);
				const nameBlock = (
					<div>
						<div style={{ fontWeight: 500, fontSize: "12px" }}>{text}</div>
						{record.test_code && <div style={{ fontSize: "11px", color: "var(--mg-text-secondary)" }}>{record.test_code}</div>}
					</div>
				);
				if (!severityData.isAbnormal) return nameBlock;
				return (
					<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
						{severityDot(severityData)}
						{nameBlock}
					</div>
				);
			},
		}, 
		{
			title: "Result",
			dataIndex: "result_value",
			key: "result_value",
			width: 50,
			align: "center",
			sorter: (a, b) => {
				const aNum = parseFloat(a.result_value);
				const bNum = parseFloat(b.result_value);
				if (isNaN(aNum) || isNaN(bNum)) return 0;
				return aNum - bNum;
			},
			render: (text, record) => (
				<div>
					<div style={resultValueStyle(severityOf(record))}>{text}</div>
					{record.units && <div style={{ fontSize: "11px", color: "var(--mg-text-secondary)" }}>{record.units}</div>}
				</div>
			),
		},
		{
			title: "Range",
			key: "range",
			width: 60,
			align: "center",
			render: (_, record) => {
				const numericValue = parseFloat(record.result_value);
				if (isNaN(numericValue)) return rangeLabel(record);

				return (
					<div style={{ width: "100%", minWidth: "120px" }}>
						<ResultRangeBar result={record} rangeContext={rangeContext} compact={true} showLabels={false} height={8} />
						<div style={{ marginTop: 4 }}>{rangeLabel(record)}</div>
					</div>
				);
			},
		},
		{
			title: "Trend",
			key: "trend",
			width: 50,
			align: "center",
			render: (_, record) => {
				if (!allTests || allTests.length === 0) return null;

				return (
					<div style={{ width: "100%", minWidth: "120px" }}>
						<HistoricalTrendChart selectedResult={record} allTests={allTests} rangeContext={rangeContext} width={200} height={90} compact={true} />
					</div>
				);
			},
		},
		{
			title: "Notes",
			key: "notes",
			width: 50,
			align: "center",
			render: (_, record) => {
				const hasTooltip = record.comments || record.definition_description || record.definition_clinical_use;
				if (!hasTooltip) return null;

				const tooltipContent = (
					<div style={{ fontSize: 11, lineHeight: 1.5 }}>
						{record.definition_description && <div style={{ marginBottom: 4 }}>{record.definition_description}</div>}
						{record.definition_clinical_use && (
							<div style={{ marginBottom: record.comments ? 4 : 0 }}>
								<strong>Clinical use:</strong> {record.definition_clinical_use}
							</div>
						)}
						{record.comments && <div>{record.comments}</div>}
					</div>
				);

				return (
					<Tooltip title={tooltipContent} placement="left">
						<InfoCircleOutlined style={{ cursor: "pointer", fontSize: "14px" }} />
					</Tooltip>
				);
			},
		},
	];

	const getStatistics = () => {
		const total = filteredResults.length;
		let normal = 0;
		let mild = 0;
		let moderate = 0;
		let severe = 0;
		let critical = 0;

		filteredResults.forEach((r) => {
			const severityData = severityOf(r);
			switch (severityData.severity) {
				case "NORMAL":
					normal++;
					break;
				case "MILD":
					mild++;
					break;
				case "MODERATE":
					moderate++;
					break;
				case "SEVERE":
					severe++;
					break;
				case "CRITICAL":
					critical++;
					break;
			}
		});

		return { total, normal, mild, moderate, severe, critical };
	};

	const stats = getStatistics();

	const emptyDescription = (globalFilter || flagFilter) ? "No results match your current filters." : "No test results available for this category.";

	return (
		<div>
			<div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "flex-start" : "center", gap: 8, marginBottom: 16 }}>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Text style={{ fontWeight: 600, fontSize: 13 }}>{category ? `${category} Results` : "Blood Test Results"}</Text>
					<Text type="secondary" style={{ fontSize: 11 }}>{filteredResults.length}</Text>
				</div>

				<div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "8px", width: isMobile ? "100%" : "auto" }}>
					<Select value={flagFilter} options={flagOptions} onChange={(value) => setFlagFilter(value)} placeholder="Filter by status" style={{ minWidth: isMobile ? "100%" : 160 }} size="small" />
					<Input value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search tests..." prefix={<SearchOutlined />} style={{ width: isMobile ? "100%" : 180 }} size="small" />
					<Button
						icon={<ClearOutlined />}
						onClick={() => {
							setGlobalFilter("");
							setFlagFilter(null);
						}}
						size="small"
						title="Clear filters"
						style={{ width: isMobile ? "100%" : "auto" }}
					>
						Clear
					</Button>
				</div>
			</div>

			<SectionCard style={{ marginBottom: 16 }}>
				<div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(6, 1fr)", gap: isMobile ? 8 : 16, textAlign: "center" }}>
					<div>
						<Text >{stats.total}</Text>
						<Text type="secondary" style={{ fontSize: 10 }}>TOTAL</Text>
					</div>
					<div>
						<Text >{stats.normal}</Text>
						<Text type="secondary" style={{ fontSize: 10 }}>NORMAL</Text>
					</div>
					<div>
						<Text >{stats.mild}</Text>
						<Text type="secondary" style={{ fontSize: 10 }}>MILD</Text>
					</div>
					<div>
						<Text >{stats.moderate}</Text>
						<Text type="secondary" style={{ fontSize: 10 }}>MODERATE</Text>
					</div>
					<div>
						<Text >{stats.severe}</Text>
						<Text type="secondary" style={{ fontSize: 10 }}>SEVERE</Text>
					</div>
					<div>
						<Text >{stats.critical}</Text>
						<Text type="secondary" style={{ fontSize: 10 }}>CRITICAL</Text>
					</div>
				</div>
			</SectionCard>

			<DataTable
				bare
				columns={isMobile ? mobileColumns : desktopColumns}
				dataSource={filteredResults}
				rowKey="id"
				onRowClick={(record) => onResultClick(record)}
				emptyTitle={(globalFilter || flagFilter) ? "No results found" : "No results"}
				emptyDescription={emptyDescription}
				scrollX={isMobile ? "100%" : undefined}
				pagination={{
					pageSize: isMobile ? 10 : 15,
					showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} results`,
					simple: isMobile,
				}}
			/>
		</div>
	);
};

export default BloodTestResultsTable;
